import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { InventoryPerformanceParsedData, InventoryPerformanceRawData } from "../../../interfaces/reports/InventoryPerformanceReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";

interface InventoryPerformanceReportProps {
    clientId: string | number;
    epochMonth: number | null;
    hierarchyOverride?: Record<string, string[]>;
    fetchDataFn?: (args: { clientId: string | number; startEpochMonth: number; endEpochMonth: number; isMtd: boolean }) => Promise<{ data?: any[]; parentLevelData?: any[] } | undefined>;
    parseDataFn?: (raw: InventoryPerformanceRawData[]) => InventoryPerformanceParsedData[];
    // New: allow injecting additional selection controls to show inline with base controls
    extraControls?: React.ReactNode;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}
 
// Moved data processing to a separate function
const processRawData = (rawData: InventoryPerformanceRawData[]): InventoryPerformanceParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Build mapping with key of classCode for items where classCode = parentClientClassCode
    const parentClassMap: Record<string, any> = {};
    rawData.forEach(item => {
        if (item.classCode === item.parentClientClassCode) {
            parentClassMap[item.classCode] = {
                imu: item.imu,
                costComplement: item.costComplement
            }
        }
    });

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        let parentMetrics = null
        if (item.parentClientClassCode && parentClassMap[item.parentClientClassCode]) {
            parentMetrics = parentClassMap[item.parentClientClassCode];
        } else {
            parentMetrics = { imu: 1, costComplement: 1 };
        }
        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode ?? null,
            category: item.category ? item.category == "" ? "N/A" : item.category : "N/A",
            categorySortOrder: item.categorySortOrder ?? null, 

            sales: new MoneyDataItem(item.sales),
            markdowns: new MoneyDataItem(item.markdowns),
            inflowCost: new MoneyDataItem(item.inflowCost),
            inflowRetail: new MoneyDataItem(item.inflowRetail),
            invAvg: new MoneyDataItem(item.invAvg),
            invAvgCost: new MoneyDataItem((item.invAvg ?? 0) * (item.costComplement || parentMetrics.costComplement)),
            invBomRm: new MoneyDataItem(item.invBomRm),
            invCurrent: new MoneyDataItem(item.invCurrent),
            imu: item.imu || parentMetrics.imu,
            costComplement: item.costComplement || parentMetrics.costComplement,
            cashMargin: new MoneyDataItem(ReportService.calculateCashMargin({ sales: item.sales ?? 0, inflowCost: item.inflowCost ?? 0, format: false })),
            grossMargin: new MoneyDataItem(ReportService.calculateGrossMargin({ sales: item.sales ?? 0, markdowns: item.markdowns ?? 0, costComplement: item.costComplement || parentMetrics.costComplement, imu: item.imu || parentMetrics.imu, format: false })),
            CMROI: '',
            GMROI: '',
            turn: '',
            efficiency: '',
            freshness: '',
        }
    });
};

const reapplyPercentCalcs = (data: InventoryPerformanceParsedData[]) => {
    // Reapply percent calculations based on the hierarchy groups
    return data.map(item => {
        const sales = (item.sales?.value || 0) as number;
        const markdowns = (item.markdowns?.value || 0) as number;
        const inflowCost = (item.inflowCost?.value || 0) as number;
        const invAvg = (item.invAvg?.value || 0) as number;
        const invBomRm = (item.invBomRm?.value || 0) as number;
        const invCurrent = (item.invCurrent?.value || 0) as number;
        const invAvgCost = (item.invAvgCost?.value || 0) as number;
        const inflowRetail = (item.inflowRetail?.value || 0) as number;

        return {
            ...item,
            CMROI: String(ReportService.calculateCmroi({ sales: sales, inflowCost: inflowCost, invAvgCost: invAvgCost })),
            GMROI: String(ReportService.calculateGmroi({ sales: sales, markdowns: markdowns, costComplement: item.costComplement, imu: item.imu, invAvgCost: invAvgCost })),
            turn: String(ReportService.calculateTurn({ sales: sales, invAvg: invAvg })),
            efficiency: String(ReportService.calculateEfficiency({ sales: sales, inventory: invCurrent, inflowRetail: inflowRetail })),
            freshness: String(ReportService.calculateFreshness({ inflowRetail: inflowRetail, inventory: invBomRm })),
        };
    });
}

const InventoryPerformanceReport = ({ clientId, epochMonth, hierarchyOverride, fetchDataFn, parseDataFn, extraControls }: InventoryPerformanceReportProps) => {
    const globalTip = TipContext.useMessage();
    const today = EpochMonthService.getTodayEpochMonth();
    const dataRef = useRef<InventoryPerformanceRawData[]>([]);
    const parentDataRef = useRef<InventoryPerformanceRawData[]>([]);
    const [parsedData, setParsedData] = useState<InventoryPerformanceParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<InventoryPerformanceParsedData[]>([]);
    const [sortedData, setSortedData] = useState<InventoryPerformanceParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(Object.values(hierarchyOverride ?? {})[0] ?? ["locationCode", "classCode"]);
    const [trendType, setTrendType] = useState<'MTD' | 'EOM'>('MTD');
    const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ? epochMonth - 12 : today - 12);
    const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ?? today);

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "Cash Margin", sortable: true },
        { name: "Gross Margin", sortable: true },
        { name: "CMROI", sortable: true },
        { name: "GMROI", sortable: true },
        { name: "Turn", sortable: true },
        { name: "Efficiency", sortable: true },
        { name: "Freshness", sortable: true }
    ];

    useEffect(() => {
        setStartEpochMonth(endEpochMonth - 12);
    }, [endEpochMonth]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const args = { clientId, startEpochMonth, endEpochMonth, isMtd: trendType === 'MTD' };
            const request = fetchDataFn
                ? fetchDataFn(args)
                : ReportService.getInventoryPerformanceReportData(args);
            request.then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    // Process both child and parent data
                    const processed = (parseDataFn ?? processRawData)(response.data ?? []);
                    const processedParent = (parseDataFn ?? processRawData)(response.parentLevelData ?? []);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    // Default: use child data
                    let sorted = ReportService.groupByHierarchy(processed, hierarchy ?? ["locationCode", "classCode"]);
                    sorted = reapplyPercentCalcs(sorted);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth, trendType, startEpochMonth, endEpochMonth]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyPercentCalcs(newSortedData);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    const tableData = useMemo(() => {
        return sortedData.map((item, index) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);

            return [
                ...labelRows,
                item.cashMargin.formatted,
                item.grossMargin.formatted,
                item.CMROI,
                item.GMROI,
                item.turn,
                item.efficiency,
                item.freshness
            ];
        });
    }, [sortedData, hierarchy]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`${EpochMonthService.epochMonthToMMMYY(startEpochMonth)} - ${EpochMonthService.epochMonthToMMMYY(endEpochMonth)} Inventory Performance Report`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Inventory Performance Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Inventory_Performance_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={hierarchyOverride ?? HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = (parseDataFn ?? processRawData)(dataRef.current);
                            setParsedData(parsedData);
                            let parsedParentData = (parseDataFn ?? processRawData)(parentDataRef.current);
                            setParentParsedData(parsedParentData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Start Period</span>
                            <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={endEpochMonth - 12} maxMonth={endEpochMonth} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">End Period</span>
                            <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={startEpochMonth} maxMonth={startEpochMonth + 12} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Trend Type</span>
                            <select
                                value={trendType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setTrendType(val === 'MTD' ? 'MTD' : 'EOM');
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="MTD">MTD</option>
                                <option value="EOM">EOM</option>
                            </select>
                        </div>
                        {/* Injected extra controls */}
                        {extraControls}
                    </div>
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        virtualize={true}
                        batchSize={300}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface InventoryPerformanceReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const InventoryPerformanceReportModal = ({ clientId, epochMonth, onClose }: InventoryPerformanceReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <InventoryPerformanceReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    InventoryPerformanceReport,
    InventoryPerformanceReportModal
}